import React, { useState } from 'react'
import { App, Button, Popconfirm, Result, Spin, Typography } from 'antd'
import { supabaseClient } from '../../lib/supabaseClient'
import { ListPage, SectionCard, StatStrip, type Stat } from '../../theme/listkit'
import { brand } from '../../theme/brand'
import { RefreshCw, UsersRound, MessagesSquare, UserCheck } from '../../components/icons'
import { fnErrorMessage } from '../../lib/fnError'

/** Shape returned by the `stream-resync-class-memberships` Edge Function. */
interface ResyncError {
  userId: string
  error: string
}
interface ResyncResult {
  ok: boolean
  processed: number
  moved: number
  removedTotal: number
  errors: ResyncError[]
}

/** Shape returned by the `stream-backfill-users` Edge Function. */
interface BackfillResult {
  ok: boolean
  total: number
  upserted: number
  errors: ResyncError[]
}

/**
 * Class Chats — chat/community operations surface. Today it hosts a single
 * one-time backfill: "Resync class memberships" reconciles every member into
 * the single class chat matching their class year by invoking the admin-gated
 * `stream-resync-class-memberships` Edge Function (the function does the Stream
 * work server-side; a non-admin caller gets a 403). Controlled state keeps the
 * action deterministic and testable.
 */
export function ClassChatsPage() {
  const { message } = App.useApp()
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<ResyncResult | null>(null)
  const [backfilling, setBackfilling] = useState(false)
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null)

  const onResync = async () => {
    setRunning(true)
    setResult(null)
    try {
      const { data, error } = await supabaseClient.functions.invoke(
        'stream-resync-class-memberships',
        { body: {} },
      )
      if (error) throw error
      const res = data as ResyncResult | null
      if (!res || res.ok !== true) {
        throw new Error('Resync did not complete successfully')
      }
      setResult(res)
      const errCount = res.errors?.length ?? 0
      if (errCount > 0) {
        message.warning(
          `Resync finished with ${errCount} error${errCount === 1 ? '' : 's'} — see details below`,
        )
      } else {
        message.success(
          `Resync complete — ${res.processed} processed, ${res.moved} moved`,
        )
      }
    } catch (e) {
      message.error(`Resync failed: ${await fnErrorMessage(e)}`)
    } finally {
      setRunning(false)
    }
  }

  // Backfill: ensure EVERY member has a Stream user object so a 1:1 DM can be
  // opened with anyone (Stream rejects a channel whose members don't exist).
  const onBackfill = async () => {
    setBackfilling(true)
    setBackfillResult(null)
    try {
      const { data, error } = await supabaseClient.functions.invoke(
        'stream-backfill-users',
        { body: {} },
      )
      if (error) throw error
      const res = data as BackfillResult | null
      if (!res || res.ok !== true) {
        throw new Error('Backfill did not complete successfully')
      }
      setBackfillResult(res)
      const errCount = res.errors?.length ?? 0
      if (errCount > 0) {
        message.warning(
          `Backfill finished with ${errCount} error${errCount === 1 ? '' : 's'} — see details below`,
        )
      } else {
        message.success(
          `Backfill complete — ${res.upserted} of ${res.total} member${res.total === 1 ? '' : 's'} provisioned`,
        )
      }
    } catch (e) {
      message.error(`Backfill failed: ${await fnErrorMessage(e)}`)
    } finally {
      setBackfilling(false)
    }
  }

  const errorCount = result?.errors?.length ?? 0
  const stats: Stat[] = result
    ? [
        { label: 'Processed', value: result.processed, icon: UsersRound, tone: 'jaguar' },
        { label: 'Moved', value: result.moved, icon: RefreshCw, tone: 'gold' },
        {
          label: 'Removed (total)',
          value: result.removedTotal,
          icon: MessagesSquare,
          tone: 'neutral',
        },
      ]
    : []

  const backfillErrorCount = backfillResult?.errors?.length ?? 0
  const backfillStats: Stat[] = backfillResult
    ? [
        { label: 'Members', value: backfillResult.total, icon: UsersRound, tone: 'jaguar' },
        { label: 'Provisioned', value: backfillResult.upserted, icon: UserCheck, tone: 'gold' },
      ]
    : []

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Chats',
        description:
          'Chat operations. Reconcile members into the class chat matching their class year, and ensure every member has a Stream chat profile so anyone can be messaged.',
      }}
    >
      <SectionCard
        title="Resync class memberships"
        sub="Re-checks every member and moves them into the single class chat matching their class year."
        style={{ maxWidth: 720 }}
      >
        <Typography.Paragraph
          type="secondary"
          style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}
        >
          This is a server-side backfill. It runs through every member, ensures each one is a
          member of exactly the class chat that matches their class year, and removes them from
          any other class chats. It is safe to re-run.
        </Typography.Paragraph>

        <Popconfirm
          title="Resync class memberships?"
          description="This re-checks every member and moves them into the single class chat matching their class year. Continue?"
          okText="Resync"
          cancelText="Cancel"
          okButtonProps={{ loading: running }}
          onConfirm={onResync}
          disabled={running}
        >
          <Button
            type="primary"
            icon={<RefreshCw size={16} color="currentColor" />}
            disabled={running}
            loading={running}
          >
            Resync class memberships
          </Button>
        </Popconfirm>

        {running && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 18,
              fontFamily: brand.fSans,
              fontSize: 13,
              color: brand.sub,
            }}
          >
            <Spin size="small" />
            <span>Reconciling every member into their class chat…</span>
          </div>
        )}

        {result && !running && (
          <div style={{ marginTop: 22 }} data-testid="resync-summary">
            <StatStrip stats={stats} columns={3} />

            <Result
              status={errorCount > 0 ? 'warning' : 'success'}
              title={
                errorCount > 0
                  ? `Resync finished with ${errorCount} error${errorCount === 1 ? '' : 's'}`
                  : 'Resync complete'
              }
              subTitle={`${result.processed} processed · ${result.moved} moved · ${result.removedTotal} removed`}
              style={{ paddingTop: 8 }}
            />

            {errorCount > 0 && (
              <div
                data-testid="resync-errors"
                style={{
                  marginTop: 4,
                  border: '1px solid ' + brand.cardLine,
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 700,
                    fontSize: 11.5,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    color: brand.sub,
                    background: brand.paper,
                    padding: '10px 14px',
                  }}
                >
                  Errors ({errorCount})
                </div>
                {result.errors.map((err, i) => (
                  <div
                    key={`${err.userId}-${i}`}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '10px 14px',
                      borderTop: i === 0 ? 'none' : '1px solid ' + brand.cardLine,
                      fontFamily: brand.fSans,
                      fontSize: 12.5,
                    }}
                  >
                    <code style={{ color: brand.jaguar, flexShrink: 0 }}>{err.userId}</code>
                    <span style={{ color: '#a4452f' }}>{err.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Backfill Stream chat profiles"
        sub="Ensure every member has a Stream user, so anyone can be messaged — even members who never opened chat."
        style={{ maxWidth: 720, marginTop: 20 }}
      >
        <Typography.Paragraph
          type="secondary"
          style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}
        >
          A 1:1 message can only open if the recipient exists as a Stream user. Members are
          normally provisioned the first time they use chat — this server-side backfill walks
          every member and provisions any that are missing, so the Message button works for the
          whole roster. It is safe to re-run.
        </Typography.Paragraph>

        <Popconfirm
          title="Backfill Stream chat profiles?"
          description="This provisions a Stream user for every member so anyone can be messaged. Continue?"
          okText="Backfill"
          cancelText="Cancel"
          okButtonProps={{ loading: backfilling }}
          onConfirm={onBackfill}
          disabled={backfilling}
        >
          <Button
            type="primary"
            icon={<UserCheck size={16} color="currentColor" />}
            disabled={backfilling}
            loading={backfilling}
          >
            Backfill Stream profiles
          </Button>
        </Popconfirm>

        {backfilling && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 18,
              fontFamily: brand.fSans,
              fontSize: 13,
              color: brand.sub,
            }}
          >
            <Spin size="small" />
            <span>Provisioning a Stream user for every member…</span>
          </div>
        )}

        {backfillResult && !backfilling && (
          <div style={{ marginTop: 22 }} data-testid="backfill-summary">
            <StatStrip stats={backfillStats} columns={2} />

            <Result
              status={backfillErrorCount > 0 ? 'warning' : 'success'}
              title={
                backfillErrorCount > 0
                  ? `Backfill finished with ${backfillErrorCount} error${backfillErrorCount === 1 ? '' : 's'}`
                  : 'Backfill complete'
              }
              subTitle={`${backfillResult.upserted} of ${backfillResult.total} member${backfillResult.total === 1 ? '' : 's'} provisioned`}
              style={{ paddingTop: 8 }}
            />

            {backfillErrorCount > 0 && (
              <div
                data-testid="backfill-errors"
                style={{
                  marginTop: 4,
                  border: '1px solid ' + brand.cardLine,
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 700,
                    fontSize: 11.5,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    color: brand.sub,
                    background: brand.paper,
                    padding: '10px 14px',
                  }}
                >
                  Errors ({backfillErrorCount})
                </div>
                {backfillResult.errors.map((err, i) => (
                  <div
                    key={`${err.userId}-${i}`}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '10px 14px',
                      borderTop: i === 0 ? 'none' : '1px solid ' + brand.cardLine,
                      fontFamily: brand.fSans,
                      fontSize: 12.5,
                    }}
                  >
                    <code style={{ color: brand.jaguar, flexShrink: 0 }}>{err.userId}</code>
                    <span style={{ color: '#a4452f' }}>{err.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
